import { Link } from '@tanstack/react-router';
import { LandingNav } from './landing/LandingNav';
import { FooterSection } from './landing/FooterSection';
import { ArrowLeft } from 'lucide-react';

export function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingNav />
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: March 2026</p>

          <div className="space-y-6 text-muted-foreground leading-relaxed">
            <p>
              This Privacy Policy describes how Easton Utilities ("we", "us", or "our") collects,
              uses, and protects your information when you use the EU ServiceLine platform
              ("Service"). By using the Service, you agree to the collection and use of
              information in accordance with this policy.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Information We Collect</h2>
            <p>We collect information that you provide directly to us, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Account Information</strong> — Name, email address, and organization details when you register or are invited to the platform.</li>
              <li><strong className="text-foreground">Support Data</strong> — Ticket content, comments, attachments, and other information submitted through the helpdesk.</li>
              <li><strong className="text-foreground">Contact Form Data</strong> — Name, email, organization, and message content submitted through our contact form.</li>
              <li><strong className="text-foreground">Usage Data</strong> — Information about how you interact with the Service, including log data, device information, and browser type.</li>
            </ul>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process and respond to support tickets and inquiries</li>
              <li>Send notifications related to your account and tickets</li>
              <li>Monitor and analyze usage patterns to improve user experience</li>
              <li>Protect against unauthorized access and ensure platform security</li>
            </ul>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Data Isolation</h2>
            <p>
              EU ServiceLine operates on a multi-tenant architecture with strict data isolation.
              Each organization's data is completely separated at the application level. No
              organization can access another organization's tickets, customers, or configuration.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Data Retention</h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide
              the Service. The platform includes automated data retention policies that remove
              old records, strip personally identifiable information from analytics data, and
              clean up expired tokens and sessions on a scheduled basis.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Third-Party Services</h2>
            <p>We use the following third-party services to operate the platform:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">SendGrid</strong> — For sending email notifications and processing inbound emails.</li>
              <li><strong className="text-foreground">Azure</strong> — For cloud hosting, database, and file storage services.</li>
            </ul>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Cookies</h2>
            <p>
              The Service uses local storage and session tokens to maintain your authentication
              state. We do not use tracking cookies for advertising purposes.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Security</h2>
            <p>
              We take reasonable measures to protect your information, including encryption in
              transit (HTTPS), secure authentication (JWT with refresh tokens), and comprehensive
              audit logging of all data access and modifications.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date.
            </p>

            <h2 className="text-xl sm:text-2xl font-semibold text-foreground pt-4">Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please{' '}
              <Link to="/" hash="contact" className="text-primary hover:underline">
                contact us
              </Link>.
            </p>
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
